import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import type PrinterAlertConfigurationModel from 'garaje/models/printer-alert-configuration';
import type UserModel from 'garaje/models/user';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { SLACK_V2_PLUGIN_KEY, MSTEAMS_V2_PLUGIN_KEY } from 'garaje/utils/enums';
import type { RecordArray } from 'garaje/utils/type-utils';

interface PrinterNotificationsComponentSignature {
  Args: {
    /**
     * set to true if there are no printer at the location
     */
    hasPrinters: boolean;
    searchUsersTask: Task<void, [string, UserModel[] | undefined]>;
  };
}

const NOTIFICATION_CHANNEL_LABEL_BY_TYPE: Record<number, string> = {
  0: 'Email',
  1: 'Slack',
  2: 'Microsoft Teams',
  3: 'SMS',
};

export default class PrinterNotificationsComponent extends Component<PrinterNotificationsComponentSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;
  @service declare store: StoreService;

  @tracked installedPluginKeys: Set<string> = new Set();
  @tracked isOpen = false;
  @tracked printerAlertConfiguration: PrinterAlertConfigurationModel | undefined;
  @tracked selectedNotificationChannels: number[] = [0];
  @tracked printerContacts: UserModel[] = [];

  PRINTER_ERROR_LABEL_BY_TYPE: Record<number, string> = {
    0: 'Paper feed issue',
    1: 'Incorrect paper size',
    2: 'Out of paper',
    3: 'Cassette missing',
    4: 'Cover open',
    5: 'Paper jam',
    7: 'Disconnected',
  };

  printerErrors = Object.keys(this.PRINTER_ERROR_LABEL_BY_TYPE);
  totalPrinterErrorCount = Object.keys(this.PRINTER_ERROR_LABEL_BY_TYPE).length;

  constructor(owner: unknown, args: PrinterNotificationsComponentSignature['Args']) {
    super(owner, args);

    void this.fetchPrinterErrorNotificationConfigsTask.perform();
    void this.fetchNotificationChannelPluginsTask.perform();
  }

  fetchPrinterErrorNotificationConfigsTask = task(async () => {
    const locationId = this.currentLocation.location.id;
    const configs = (await this.store.query('printer-alert-configuration', {
      filter: { location: locationId },
    })) as RecordArray<PrinterAlertConfigurationModel>;

    let config = configs.slice()[0];
    if (!config) {
      config = this.store.createRecord('printer-alert-configuration', {
        location: this.currentLocation.location,
        enabled: false,
        errorTypes: [],
        recipientIds: [],
        notificationChannels: [0],
      });
    }
    this.printerAlertConfiguration = config;

    if (config.recipientIds?.length) {
      const userPromises = config.recipientIds.map((id) => this.store.findRecord('user', id));
      const users = await Promise.all(userPromises);
      this.printerContacts = users;
    }
  });

  // ENABLE/DISABLE NOTIFICATIONS

  @action
  enableNotifications(): void {
    this.isOpen = true;
    this.printerAlertConfiguration!.enabled = true;
    void this.savePrinterErrorNotificationConfigsTask.perform();
  }

  @action
  disableNotifications(): void {
    this.isOpen = false;
    this.printerAlertConfiguration!.enabled = false;
    void this.savePrinterErrorNotificationConfigsTask.perform();
  }

  // NOTIFICATION CHANNELS

  fetchNotificationChannelPluginsTask = task(async () => {
    const plugins = await this.store.query('plugin', {
      filter: { notification_channels: true },
      page: { limit: 100 },
    });
    this.installedPluginKeys = new Set(plugins.map((plugin) => plugin.key));
  });

  get hasSlackAvailable(): boolean {
    return this.installedPluginKeys.has(SLACK_V2_PLUGIN_KEY);
  }

  get hasMSTeamsAvailable(): boolean {
    return this.installedPluginKeys.has(MSTEAMS_V2_PLUGIN_KEY);
  }

  get hasSmsAvailable(): boolean {
    return Boolean(this.state.vrSubscription?.canAccessSmsNotification);
  }

  get isEnabled(): boolean {
    return Boolean(this.printerAlertConfiguration?.enabled);
  }

  get notificationChannels(): { id: number; label: string }[] {
    return Object.keys(NOTIFICATION_CHANNEL_LABEL_BY_TYPE).reduce<{ id: number; label: string }[]>((acc, id) => {
      const channelId = Number(id);
      const channel = { id: channelId, label: NOTIFICATION_CHANNEL_LABEL_BY_TYPE[channelId]! };
      switch (channelId) {
        case 0:
          acc.push(channel);
          break;
        case 1:
          if (this.hasSlackAvailable) {
            acc.push(channel);
          }
          break;
        case 2:
          if (this.hasMSTeamsAvailable) {
            acc.push(channel);
          }
          break;
        case 3:
          if (this.hasSmsAvailable) {
            acc.push(channel);
          }
          break;
      }

      return acc;
    }, []);
  }

  @action
  toggleNotificationChannel(channelId: number): void {
    const current = this.printerAlertConfiguration!.notificationChannels;
    const updated = current.includes(channelId) ? current.filter((id) => id !== channelId) : [...current, channelId];
    this.printerAlertConfiguration!.notificationChannels = updated;
  }

  // PRINTER ERRORS

  get availablePrinterErrors(): string[] {
    const selected = this.printerAlertConfiguration?.errorTypes ?? [];
    const available = this.printerErrors.filter((error) => !selected.includes(Number(error)));
    return available;
  }

  get selectedPrinterErrors(): number[] {
    return this.printerAlertConfiguration!.errorTypes;
  }

  @action
  onPrinterErrorSelect(errors: string[]): void {
    this.printerAlertConfiguration!.errorTypes = errors.map((error) => Number(error));
  }

  searchTask = task(async (term: string) => {
    return await this.args.searchUsersTask.perform(term, this.printerContacts);
  });

  @action
  addPrinterContacts(users: UserModel[]): void {
    this.printerContacts = users;
    this.printerAlertConfiguration!.recipientIds = users.map((user) => Number(user.id));
  }

  // SAVE NOTIFICATION CONFIGS

  get hasChanges(): boolean {
    const config = this.printerAlertConfiguration;
    if (!config?.hasDirtyAttributes) {
      return false;
    }

    // New (unsaved) records report all default values as changes (originalValue = undefined).
    // Only count user-selected fields to avoid false positives from defaults like notificationChannels: [0].
    if (<boolean>(<unknown>config.isNew)) {
      return config.errorTypes.length > 0 || config.recipientIds.length > 0;
    }

    // For persisted records, compare changed attributes with type-aware logic
    const changes = config.changedAttributes();

    return Object.keys(changes).some((key) => {
      const change = changes[key];
      if (!change) return false;

      const [originalValue, newValue] = change as [unknown, unknown];

      if (Array.isArray(originalValue) || Array.isArray(newValue)) {
        const origArr = Array.isArray(originalValue) ? (originalValue as number[]) : [];
        const newArr = Array.isArray(newValue) ? (newValue as number[]) : [];
        return !this.arraysEqual(origArr, newArr);
      }

      return originalValue !== newValue;
    });
  }

  arraysEqual(a: number[], b: number[]): boolean {
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    if (sortedA.length !== sortedB.length) return false;

    return sortedA.every((value, index) => value === sortedB[index]);
  }

  savePrinterErrorNotificationConfigsTask = task(async () => {
    const config = this.printerAlertConfiguration;
    if (!config || !config.hasDirtyAttributes) {
      return;
    }
    await config.save();
    this.flashMessages.showAndHideFlash('success', 'Settings updated');
  });
}
